import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { useLocalStorage } from 'usehooks-ts'

import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'

const LayoutStateContext = createContext(undefined)

export const useLayoutState = () => useContext(LayoutStateContext)

/**
 * Provider to manage the header/sidebar state of the app.
 *
 * Desktop: sidebar is always pinned, either 'full' (text + icons) or 'mini' (icons only).
 * Small screen: sidebar behaves as a drawer toggled open/closed over a backdrop.
 *
 * Exposes:
 * - sidebarOpen: on desktop, true when full; on small screen, true when drawer is open
 * - sidebarCollapsed: desktop-only flag — true when in mini mode
 * - setSidebarOpen / setSidebarClosed: toggle state (desktop pref persisted to localStorage)
 * - isSmallScreen: whether viewport is below md breakpoint
 */
export default function LayoutStateProvider({ children }) {
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))

  // Desktop preference: 'full' (default) | 'mini'
  const [desktopVariant, setDesktopVariant] = useLocalStorage('sidebar-variant', 'full')
  // Small-screen drawer state (transient)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const setSidebarOpen = useCallback(() => {
    if (isSmallScreen) setDrawerOpen(true)
    else setDesktopVariant('full')
  }, [isSmallScreen, setDesktopVariant])

  const setSidebarClosed = useCallback(() => {
    if (isSmallScreen) setDrawerOpen(false)
    else setDesktopVariant('mini')
  }, [isSmallScreen, setDesktopVariant])

  const sidebarOpen = isSmallScreen ? drawerOpen : desktopVariant !== 'mini'
  const sidebarCollapsed = !isSmallScreen && desktopVariant === 'mini'

  const value = useMemo(
    () => ({
      sidebarOpen,
      sidebarCollapsed,
      setSidebarOpen,
      setSidebarClosed,
      isSmallScreen,
    }),
    [sidebarOpen, sidebarCollapsed, setSidebarOpen, setSidebarClosed, isSmallScreen]
  )

  return <LayoutStateContext.Provider value={value}>{children}</LayoutStateContext.Provider>
}
